import { z } from 'zod'

/**
 * FO-361 (batch-41) - netto-bijtelling: parametertabel per belastingjaar,
 * overzicht- en simulatie-DTO's. Gedeeld tussen API (validatie + read-DTO) en
 * backoffice (form-validatie + rendering). Eén bron: het form kán niets sturen
 * dat de API weigert.
 *
 * De pure rekenlogica leeft in `@streep/utils` (`berekenNettoBijtelling`);
 * percentages reizen als heel-procent (35.82), bedragen als getal in euro
 * (Decimal in de DB, number in de DTO), datums als ISO-string.
 *
 * PRIVACY (spec 63 §5): netto-bedragen en het (voorgevulde) jaarinkomen zijn
 * loongevoelig. In `BijtellingOverzichtRijDTO` is het `netto`-veld ALLEEN
 * aanwezig wanneer de salaris-gate (ADMIN/HR/SALARIS) passeert - server-side
 * weggelaten, niet UI-verborgen (T10).
 */

// ─── Disclaimer (op ELKE netto-weergave, spec 63 §2/§8) ──────────────────────

export const BIJTELLING_INDICATIE_DISCLAIMER =
  'Indicatie op basis van het geregistreerde salaris - Nmbrs blijft de bron voor de werkelijke inhouding.'

// ─── Canonieke seed-data per belastingjaar ───────────────────────────────────
//
// EEN bron voor de geseede officiële Belastingdienst-cijfers: de db-seed
// (`seed-bijtelling-netto-parameters.ts`) schrijft deze waarden en de
// referentie-narekening-fixtures (utils- + api-tests) asserten ertegen, zodat
// een seed-typefout de op-de-euro-fixture breekt (spec 63 §12.5). Bronnen +
// verificatiedatum staan in het seed-script; wijzig deze cijfers alleen samen
// met de fixture-verwachtingen.

export interface BijtellingNettoSeedJaar {
  jaar: number
  ahkMaximum: number
  ahkAfbouwStart: number
  ahkAfbouwPct: number
  youngtimerPct: number
  youngtimerGrensJaren: number
  ibSchijven: ReadonlyArray<{ totInkomen: number | null; tariefPct: number }>
  akSegmenten: ReadonlyArray<{ vanInkomen: number; basisBedrag: number; percentagePct: number }>
}

export const BIJTELLING_NETTO_PARAMETERS_SEED_DATA: ReadonlyArray<BijtellingNettoSeedJaar> = [
  {
    jaar: 2025,
    // AHK 2025: EUR 3.068 max; afbouw 6,337% vanaf EUR 28.406 (EUR 0 vanaf 76.818).
    ahkMaximum: 3068,
    ahkAfbouwStart: 28406,
    ahkAfbouwPct: 6.337,
    youngtimerPct: 35,
    youngtimerGrensJaren: 15,
    // Box 1 2025: 35,82% t/m 38.441; 37,48% t/m 76.817; 49,50% daarboven.
    ibSchijven: [
      { totInkomen: 38441, tariefPct: 35.82 },
      { totInkomen: 76817, tariefPct: 37.48 },
      { totInkomen: null, tariefPct: 49.5 },
    ],
    // Arbeidskorting 2025 (officiële tabel, niet AOW).
    akSegmenten: [
      { vanInkomen: 0, basisBedrag: 0, percentagePct: 8.053 },
      { vanInkomen: 12169, basisBedrag: 980, percentagePct: 30.03 },
      { vanInkomen: 26288, basisBedrag: 5220, percentagePct: 2.258 },
      { vanInkomen: 43071, basisBedrag: 5599, percentagePct: -6.51 },
      { vanInkomen: 129078, basisBedrag: 0, percentagePct: 0 },
    ],
  },
  {
    jaar: 2026,
    // AHK 2026: EUR 3.115 max; afbouw 6,398% vanaf EUR 29.736 (EUR 0 vanaf 78.427).
    ahkMaximum: 3115,
    ahkAfbouwStart: 29736,
    ahkAfbouwPct: 6.398,
    youngtimerPct: 35,
    youngtimerGrensJaren: 15,
    // Box 1 2026: 35,75% t/m 38.883; 37,56% t/m 78.426; 49,50% daarboven.
    ibSchijven: [
      { totInkomen: 38883, tariefPct: 35.75 },
      { totInkomen: 78426, tariefPct: 37.56 },
      { totInkomen: null, tariefPct: 49.5 },
    ],
    // Arbeidskorting 2026 (officiële tabel, niet AOW).
    akSegmenten: [
      { vanInkomen: 0, basisBedrag: 0, percentagePct: 8.324 },
      { vanInkomen: 11965, basisBedrag: 996, percentagePct: 31.009 },
      { vanInkomen: 25845, basisBedrag: 5300, percentagePct: 1.95 },
      { vanInkomen: 45592, basisBedrag: 5685, percentagePct: -6.51 },
      { vanInkomen: 132920, basisBedrag: 0, percentagePct: 0 },
    ],
  },
]

// ─── Parametertabel per belastingjaar (beheer-schema) ────────────────────────

export const bijtellingIbSchijfSchema = z.object({
  /** Bovengrens (t/m) in euro; null = onbegrensd (alleen de laatste schijf). */
  totInkomen: z.number().positive().nullable(),
  /** Tarief als heel-procent (bv. 35.82). */
  tariefPct: z.number().min(0).max(100),
})

export const bijtellingAkSegmentSchema = z.object({
  /** Ondergrens van het segment (arbeidsinkomen vanaf), in euro. */
  vanInkomen: z.number().min(0),
  /** Arbeidskorting op de ondergrens, in euro. */
  basisBedrag: z.number().min(0),
  /** Heel-procent over het meerdere; negatief = afbouwtraject (bv. -6.51). */
  percentagePct: z.number().min(-100).max(100),
})

/**
 * Beheer-body voor één belastingjaar. Refinements bewaken spec 63 §6: schijven
 * aaneensluitend en strikt oplopend (alleen de laatste onbegrensd), percentages
 * 0-100, AK-segmenten strikt oplopend en beginnend bij 0.
 */
export const bijtellingNettoParametersSchema = z
  .object({
    jaar: z.number().int().min(2000).max(2100),
    ahkMaximum: z.number().min(0).max(100_000),
    ahkAfbouwStart: z.number().min(0).max(10_000_000),
    ahkAfbouwPct: z.number().min(0).max(100),
    /** Youngtimer (spec 63 §7.1): percentage over de dagwaarde. */
    youngtimerPct: z.number().min(0).max(100),
    /** Leeftijdsgrens in jaren (> grens = youngtimer). */
    youngtimerGrensJaren: z.number().int().positive().max(100).default(15),
    ibSchijven: z.array(bijtellingIbSchijfSchema).min(1).max(10),
    akSegmenten: z.array(bijtellingAkSegmentSchema).min(1).max(10),
  })
  .superRefine((d, ctx) => {
    // IB-schijven: strikt oplopend; precies de laatste heeft totInkomen null.
    for (let i = 0; i < d.ibSchijven.length; i++) {
      const s = d.ibSchijven[i]
      const isLaatste = i === d.ibSchijven.length - 1
      if (isLaatste && s.totInkomen !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ibSchijven', i, 'totInkomen'],
          message: 'De laatste schijf moet onbegrensd zijn (geen bovengrens)',
        })
      }
      if (!isLaatste) {
        if (s.totInkomen === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ibSchijven', i, 'totInkomen'],
            message: 'Alleen de laatste schijf mag onbegrensd zijn',
          })
        } else {
          const vorige = i > 0 ? d.ibSchijven[i - 1].totInkomen : 0
          if (vorige !== null && s.totInkomen <= vorige) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['ibSchijven', i, 'totInkomen'],
              message: 'Schijfgrenzen moeten strikt oplopen',
            })
          }
        }
      }
    }
    // AK-segmenten: strikt oplopend op vanInkomen, eerste begint op 0.
    if (d.akSegmenten.length > 0 && d.akSegmenten[0].vanInkomen !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['akSegmenten', 0, 'vanInkomen'],
        message: 'Het eerste arbeidskorting-segment moet bij 0 beginnen',
      })
    }
    for (let i = 1; i < d.akSegmenten.length; i++) {
      if (d.akSegmenten[i].vanInkomen <= d.akSegmenten[i - 1].vanInkomen) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['akSegmenten', i, 'vanInkomen'],
          message: 'Segment-ondergrenzen moeten strikt oplopen',
        })
      }
    }
  })
export type BijtellingNettoParametersInput = z.infer<typeof bijtellingNettoParametersSchema>

/** Read-DTO van één belastingjaar-parameterset. */
export interface BijtellingNettoParametersDTO {
  id: string
  jaar: number
  ahkMaximum: number
  ahkAfbouwStart: number
  ahkAfbouwPct: number
  youngtimerPct: number
  youngtimerGrensJaren: number
  ibSchijven: Array<{ totInkomen: number | null; tariefPct: number }>
  akSegmenten: Array<{ vanInkomen: number; basisBedrag: number; percentagePct: number }>
  createdAt: string
  updatedAt: string
}

/** Audit-regel voor de parametertabel (wie/wanneer/van→naar). */
export interface BijtellingNettoParameterAuditDTO {
  id: string
  jaar: number
  actorNaam: string | null
  action: 'CREATE' | 'UPDATE' | 'DELETE'
  changes: Record<string, { old: unknown; new: unknown }> | null
  createdAt: string
}

// ─── Simulator (spec 63 §4.2 + §6) ───────────────────────────────────────────

/**
 * Simulatie-request. Overrides wijzigen alleen de simulatie (er wordt niets
 * opgeslagen). Zonder salaris-gate rekent de API uitsluitend met het handmatige
 * `brutoJaarinkomenOverride` (T11) - de cache wordt dan nooit gelezen.
 */
export const bijtellingSimulatieSchema = z.object({
  voertuigId: z.string().min(1).nullable().optional(),
  employeeId: z.string().min(1).nullable().optional(),
  /** Belastingjaar; default het huidige jaar (Europe/Amsterdam). */
  jaar: z.number().int().min(2000).max(2100).optional(),
  /** Override cataloguswaarde (> 0). */
  cataloguswaardeOverride: z.number().positive().max(5_000_000).nullable().optional(),
  /** Override bruto jaarinkomen (0 - 500.000). */
  brutoJaarinkomenOverride: z.number().min(0).max(500_000).nullable().optional(),
  /** Override eigen bijdrage per maand (0 - 2.500). */
  eigenBijdrageMaandOverride: z.number().min(0).max(2_500).nullable().optional(),
  /** Effect heffingskortingen meerekenen (default aan, zoals berekenhet.nl). */
  kortingenMeerekenen: z.boolean().default(true),
})
export type BijtellingSimulatieInput = z.infer<typeof bijtellingSimulatieSchema>

// ─── Overzicht- en simulatie-DTO's ───────────────────────────────────────────

/** Doorklik naar de plek waar de ontbrekende data aangevuld wordt (§4.1). */
export interface BijtellingFixLinkDTO {
  soort: 'VOERTUIG' | 'MEDEWERKER'
  id: string
  label: string
}

export type BijtellingBrutoStatus = 'BEREKEND' | 'ONVOLLEDIG'

/** Bruto-blok: volgt uit publieke autodata + contract-eigen-bijdrage (§5). */
export interface BijtellingBrutoDTO {
  status: BijtellingBrutoStatus
  reden: string | null
  fixLink: BijtellingFixLinkDTO | null
  /** Bv. "18% tot € 30.000 / 22% erboven", "22% vlak", "35% over dagwaarde". */
  tariefLabel: string | null
  isYoungtimer: boolean
  laagPercentage: number | null
  capBedrag: number | null
  hoogPercentage: number | null
  /** Einde 60-maanden-klik (ISO-datum); ook gevuld als al verstreken (§7.2). */
  vervaltOp: string | null
  /** Waarschuwingsbadge: verval binnen 6 maanden (sluit aan op de verval-cron). */
  vervaltBinnenkort: boolean
  /** 60 maanden verstreken: het algemene tarief geldt (T8). */
  isVervallen: boolean
  brutoJaar: number | null
  brutoMaand: number | null
  /** Eigen bijdrage privégebruik per maand (contract-mutatie, spec 31/40). */
  eigenBijdrageMaand: number | null
  /** Belaste bijtelling: max(0, bruto - eigen bijdrage x 12). */
  belastJaar: number | null
}

export type BijtellingNettoStatus = 'BEREKEND' | 'ONVOLLEDIG' | 'GEEN_PARAMETERS'

/** Herleidbare opbouw (PayrollOpbouwPaneel-filosofie): reeds berekend, UI rekent niets. */
export interface BijtellingNettoOpbouwDTO {
  belasteBijtellingJaar: number
  eigenBijdrageJaar: number
  ibZonderBijtelling: number
  ibMetBijtelling: number
  schijfVerschil: number
  ahkZonderBijtelling: number
  ahkMetBijtelling: number
  ahkVerlies: number
  arbeidskorting: number
  heffingZonderBijtelling: number
  heffingMetBijtelling: number
  kortingenEffect: number
  /** Opbouw van het gebruikte jaarinkomen (maandloon x 12 x factor). */
  maandloon: number | null
  vakantiegeldPct: number | null
  dertiendeMaandPct: number | null
}

/**
 * Netto-blok. LOONGEVOELIG: in het overzicht alleen aanwezig mét salaris-gate;
 * in de simulatie ook zonder gate wanneer het inkomen handmatig is ingevuld
 * (dan onthult de response niets dat de gebruiker niet zelf intypte, T11).
 */
export interface BijtellingNettoDTO {
  status: BijtellingNettoStatus
  reden: string | null
  fixLink: BijtellingFixLinkDTO | null
  /** Gebruikt bruto jaarinkomen (zonder bijtelling). */
  jaarinkomen: number | null
  nettoPerMaand: number | null
  nettoPerJaar: number | null
  kortingenMeegerekend: boolean
  opbouw: BijtellingNettoOpbouwDTO | null
}

/** Eén overzichtsrij: voertuig x actieve bijtellingsplichtige bestuurder (§4.1). */
export interface BijtellingOverzichtRijDTO {
  voertuigId: string
  kenteken: string
  merk: string
  model: string
  brandstof: string
  cataloguswaarde: number | null
  dagwaarde: number | null
  toewijzingId: string
  employeeId: string
  employeeNaam: string
  bruto: BijtellingBrutoDTO
  /** ALLEEN aanwezig met salaris-gate (server-side weggelaten, T10). */
  netto?: BijtellingNettoDTO
}

export interface BijtellingOverzichtMetaDTO {
  /** Peiljaar (Europe/Amsterdam) waarvoor de netto-indicatie rekent. */
  jaar: number
  /** false = parametertabel voor dit jaar ontbreekt (T12: nette melding). */
  parametersBeschikbaar: boolean
  /** Salaris-gate-status van de aanvrager (bepaalt of `netto` aanwezig is). */
  magNettoZien: boolean
  disclaimer: string
}

export interface BijtellingOverzichtResponseDTO {
  data: BijtellingOverzichtRijDTO[]
  meta: BijtellingOverzichtMetaDTO
}

/** Simulatie-resultaat: zelfde blokken als één overzichtsrij (§4.2). */
export interface BijtellingSimulatieResultDTO {
  jaar: number
  parametersBeschikbaar: boolean
  magNettoZien: boolean
  disclaimer: string
  bruto: BijtellingBrutoDTO | null
  /** null zolang er geen voertuig(data) of geen inkomen beschikbaar is. */
  netto: BijtellingNettoDTO | null
  /**
   * Voorinvulling uit de app-data - ALLEEN aanwezig met salaris-gate (het
   * jaarinkomen verraadt het salaris; zonder gate ontbreekt de key volledig).
   */
  voorgevuld?: {
    jaarinkomen: number | null
    maandloon: number | null
  }
  /** Voorinvulling zonder loon-lek (eigen bijdrage + cataloguswaarde/dagwaarde). */
  voertuigWaarden: {
    cataloguswaarde: number | null
    dagwaarde: number | null
    eigenBijdrageMaand: number | null
  } | null
}
